import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BookOpenText, ChevronDown, ChevronRight } from 'lucide-react'

// Collapsible guides on the chart of accounts, transaction accounts and
// transaction types pages (and, through Guide and Part, the jobs pages), in plain words with worked examples. Each remembers whether it was left
// open, per browser.

const load = (key) => { try { return localStorage.getItem(key) === 'open' } catch { return false } }
const save = (key, open) => { try { localStorage.setItem(key, open ? 'open' : 'closed') } catch { /* private window */ } }

export function Guide({ id, title, intro, children }) {
  const key = `asys.guide.${id}`
  const [open, setOpen] = useState(() => load(key))
  const toggle = () => setOpen((o) => { save(key, !o); return !o })
  return (
    <section className={`mf-guide${open ? ' open' : ''}`}>
      <button type="button" className="mf-guide-head" aria-expanded={open} onClick={toggle}>
        <BookOpenText size={16} aria-hidden="true" />
        <span className="mf-guide-title">{title}</span>
        <span className="mf-guide-toggle">{open ? 'Hide' : 'Show'}{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
      </button>
      {open && (
        <div className="mf-guide-body">
          {intro && <p className="mf-guide-intro">{intro}</p>}
          {children}
        </div>
      )}
    </section>
  )
}

export function Part({ title, open, children }) {
  return (
    <details className="mf-guide-part" open={open}>
      <summary>{title}</summary>
      <div className="mf-guide-text">{children}</div>
    </details>
  )
}

/** Debit/credit lines of an example entry: [[account, debit, credit], ...]. */
function Entry({ lines }) {
  return (
    <table className="mf-guide-table">
      <thead><tr><th>Account</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
      <tbody>
        {lines.map(([acct, dr, cr]) => <tr key={acct}><td>{acct}</td><td className="num">{dr}</td><td className="num">{cr}</td></tr>)}
      </tbody>
    </table>
  )
}

/** How a transaction type is set up: [[side, account, share], ...]. */
function Setup({ lines }) {
  return (
    <table className="mf-guide-table">
      <thead><tr><th>Side</th><th>Account</th><th>Share</th></tr></thead>
      <tbody>
        {lines.map(([side, acct, share]) => <tr key={side + acct}><td>{side}</td><td>{acct}</td><td>{share}</td></tr>)}
      </tbody>
    </table>
  )
}

export const Step = ({ children }) => <p className="mf-guide-step">{children}</p>

export function ChartGuide() {
  return (
    <Guide id="chart" title="Guide: how the chart of accounts works"
      intro="The chart of accounts is the list of buckets your books sort every amount into. You rarely need to change it: the standard accounts cover most shops. Open a topic below.">
      <Part title="The five kinds of account" open>
        <ul>
          <li><strong>Assets (1000s):</strong> what the business owns, from the most liquid down: cash and bank, money customers owe you, work done but not yet billed, stock, prepaid costs, then property and equipment.</li>
          <li><strong>Liabilities (2000s):</strong> what it owes, soonest due first: vendors’ bills, accrued costs, sales tax collected, customer deposits, then loans.</li>
          <li><strong>Equity (3000s):</strong> the owners’ stake: money put in, profit kept in the business (retained earnings), less drawings or dividends taken out.</li>
          <li><strong>Revenue (4000s):</strong> what you earn from selling products and services. Other income, such as interest, is in the 7000s.</li>
          <li><strong>Expenses (5000s to 8000s):</strong> what it costs: the cost of what you sold (5000s), running costs such as rent, wages and fees (6000s), other expenses such as interest (7100s) and income tax (8000s).</li>
        </ul>
        <p>Assets, liabilities and equity make up the <Link to="/accounting/reports/balance-sheet">balance sheet</Link>. Revenue and expenses make up the <Link to="/accounting/reports/income-statement">income statement</Link>.</p>
      </Part>
      <Part title="Headings and the accounts under them">
        <p>Codes ending in 00 are <strong>headings</strong>, such as 6000 Operating expenses. The accounts under a heading share its first two digits (6001 to 6099) and its type.</p>
        <p>Amounts are posted to the accounts under a heading, not to the heading itself. On this page a heading shows the total of its group.</p>
      </Part>
      <Part title="Adding an account">
        <ol>
          <li>Find the heading it belongs under, for example Operating expenses for a running cost such as rent.</li>
          <li>Click <em>Add an account under …</em> below that heading. The next free code is filled in for you.</li>
          <li>Give it a clear name, such as Rent or Fuel, and click Add account.</li>
        </ol>
        <p>It can be used straight away in Expenses, journal entries and invoices. For a new heading, choose <em>New account</em>, leave <em>Under heading</em> as None and give it a code ending in 00.</p>
      </Part>
      <Part title="Changing or removing an account">
        <p>You can rename any account. Once anything is posted to an account, or it’s a default account, its <strong>code and type are fixed</strong>, because past entries keep the code they were posted with.</p>
        <p>To reorganise, add the new account and move the balance across with a <Link to="/accounting/journal-entries">journal entry</Link>. An account nothing uses can be deleted from its page.</p>
      </Part>
      <Part title="Opening balances when you move to ASYS">
        <p>If you’re bringing your books from another system, give each account the balance it had on your start date: on the account’s page, or all at once under <Link to="/master/opening-balances">Opening balances</Link>. See the guide there.</p>
      </Part>
      <Part title="Where an account shows on the statements">
        <p>Each account is placed in a section of the statements automatically: current assets, operating expenses and so on. If one is in the wrong place, an accountant can move it under <Link to="/accounting/reports/classification">Account classification</Link>.</p>
      </Part>
    </Guide>
  )
}

export function DefaultsGuide() {
  return (
    <Guide id="defaults" title="Guide: how transaction accounts work"
      intro="You don’t write journal entries for everyday work. When you make a sale, receive stock or pay a bill, ASYS Retail writes the entry for you. This page says which accounts each kind of transaction uses.">
      <Part title="A worked example" open>
        <p>At the POS you sell an item for $100 in cash, with $7 sales tax. The item cost you $60. The app posts:</p>
        <Entry lines={[['Cash taken', '$107.00', ''], ['Products sold', '', '$100.00'], ['Sales tax owed', '', '$7.00'], ['Cost of goods sold', '$60.00', ''], ['Stock', '', '$60.00']]} />
        <p>Each of these is a row under <em>POS sales</em>. Choose a different account in a row and the next sale uses it; what’s already posted stays where it was.</p>
      </Part>
      <Part title="Each transaction has its own accounts">
        <p>Each card is one kind of transaction, with the accounts it posts to. Some can differ from transaction to transaction, for example:</p>
        <ul>
          <li>products sold on invoices can go to a different income account from products sold at the POS;</li>
          <li>customers’ transfers can go to one bank account and card payments at the POS to another;</li>
          <li>expenses can start on the account you use most, such as Rent.</li>
        </ul>
        <p>A row you haven’t changed shows the standard account it uses, marked <em>(standard)</em>.</p>
      </Part>
      <Part title="Shared accounts">
        <p>A few accounts must be the same in every transaction that uses them, so the amounts owed and the stock stay in step. They’re marked <strong>Shared</strong>, and changing one changes it everywhere:</p>
        <ul>
          <li><strong>Accounts receivable</strong> (owed by customers): invoices, customer payments, credit memos and sales on account;</li>
          <li><strong>Accounts payable</strong> (owed to vendors): bills, vendor payments and expenses on account;</li>
          <li><strong>Stock</strong>, <strong>Cost of goods sold</strong> and <strong>Sales tax owed</strong>: POS sales, invoices, receiving and stock counts.</li>
        </ul>
        <p>If invoices were owed on one account but payments cleared another, customers’ balances would stop adding up.</p>
      </Part>
      <Part title="Does every account need to be here?">
        <p>No. An account you add to the <Link to="/master/accounts">chart of accounts</Link> is ready to use straight away: pick it on an expense, a journal entry, a transaction type or an item.</p>
        <p>This page is only for the accounts the app picks by itself when it writes an entry.</p>
      </Part>
      <Part title="Choosing on the day">
        <p>Some transactions let you pick another account as you record them:</p>
        <ul>
          <li><strong>Payments:</strong> <em>Deposit to</em> on a customer payment, <em>Pay from</em> on a vendor payment;</li>
          <li><strong>Invoices:</strong> the <em>Receivable account</em> on an invoice;</li>
          <li><strong>Bills:</strong> the payable account when a bill is posted;</li>
          <li><strong>Expenses:</strong> the accounts on an expense.</li>
        </ul>
        <p>Under <em>Extra accounts to choose from</em>, add more cash, bank, receivable or payable accounts to offer there, such as a second bank account (1003) or Staff advances (1103). Add the account in the chart of accounts first.</p>
      </Part>
      <Part title="POS at each store">
        <p>A store can use its own cash drawer or bank account for its sales and refunds at the POS. A store left on <em>The POS account</em> uses the one under POS sales.</p>
      </Part>
      <Part title="An item’s own income account">
        <p>On an item’s page, <em>Income account</em> sends its sales to an account of its own, for example Gift wrapping income, at the POS and on invoices. Items left on <em>Default</em> use Products sold (or Services sold) for POS sales or invoices.</p>
      </Part>
      <Part title="If an account isn’t set">
        <p>The app uses the standard account for it, such as 1101 for Accounts receivable, if your chart of accounts has it. If it doesn’t, the transactions that need it stop with a message saying which one to set, and nothing is saved.</p>
        <p><Link to="/master/setup">Account setup</Link> adds the standard accounts and fills in what’s missing, without changing your choices.</p>
      </Part>
      <Part title="Transactions the app doesn’t post for you">
        <p>For anything else you record often, such as gift cards sold or tips paid out, set up a <Link to="/accounting/transaction-types">transaction type</Link> with its own accounts, then record it with just an amount and a date.</p>
      </Part>
    </Guide>
  )
}

export function TransactionTypesGuide() {
  return (
    <Guide id="transaction-types" title="Guide: how transaction types work"
      intro="A transaction type is a recipe for a journal entry you make often. You choose the accounts once; after that you only enter the amount, and the app writes the entry. Open a topic below.">
      <Part title="When to use one" open>
        <p>The app already writes the entries for sales, invoices, payments, stock and vendor bills. Use a transaction type for anything else you record again and again, for example:</p>
        <ul>
          <li>gift cards sold</li>
          <li>rent shared between your stores</li>
          <li>a loan repayment</li>
          <li>money the owner puts into the business</li>
        </ul>
        <p>For something you’ll only record once, a plain <Link to="/accounting/journal/new">journal entry</Link> is simpler. If you need an account that isn’t in the <Link to="/master/accounts">chart of accounts</Link> yet (such as Gift cards owed), add it there first.</p>
      </Part>
      <Part title="Example 1: gift cards sold (the simplest kind)">
        <p>A customer pays $50 cash for a gift card. You have the cash, but you owe them $50 of goods until they use the card, so the other side is a liability.</p>
        <Step>Set it up once</Step>
        <Setup lines={[['Debit', 'Cash', 'The rest'], ['Credit', 'Gift cards owed (a liability)', 'The rest']]} />
        <Step>Record it with an amount of $50 and the app posts:</Step>
        <Entry lines={[['Cash', '$50.00', ''], ['Gift cards owed', '', '$50.00']]} />
        <p>With one line on each side, each line simply takes the whole amount.</p>
      </Part>
      <Part title="Debit or credit? A quick rule">
        <table className="mf-guide-table">
          <thead><tr><th>What happens</th><th>Put the account on the</th></tr></thead>
          <tbody>
            <tr><td>Money comes into cash or the bank</td><td>Debit side</td></tr>
            <tr><td>Money goes out of cash or the bank</td><td>Credit side</td></tr>
            <tr><td>You spend on a cost (rent, wages, interest)</td><td>Debit side</td></tr>
            <tr><td>You earn income</td><td>Credit side</td></tr>
            <tr><td>You now owe someone (a loan, a gift card)</td><td>Credit side</td></tr>
            <tr><td>You pay off what you owe</td><td>Debit side</td></tr>
            <tr><td>The owner puts money in</td><td>Credit side (owner’s equity)</td></tr>
          </tbody>
        </table>
        <p>Every entry has at least one debit and one credit, and the two sides always come to the same total.</p>
      </Part>
      <Part title="Splitting the amount: the rest, percent and fixed">
        <p>Each side can have several lines. You enter one amount, and every line takes a share of it:</p>
        <ul>
          <li><strong>The rest:</strong> whatever is left. Exactly one line on each side must be <em>the rest</em>, so the side always adds up to the amount.</li>
          <li><strong>Percent:</strong> that percentage of the amount, such as 60%.</li>
          <li><strong>Fixed amount:</strong> the same sum every time, such as $50.</li>
        </ul>
        <p>The editor shows what $100 would post while you set it up, so you can check the split before saving.</p>
      </Part>
      <Part title="Example 2: rent shared between two stores (percent)">
        <p>You pay $2,000 rent a month from the bank. The Abuja store uses 60% of the space and Kano the rest.</p>
        <Step>Set it up once</Step>
        <Setup lines={[['Debit', 'Rent, Abuja', '60%'], ['Debit', 'Rent, Kano', 'The rest'], ['Credit', 'Bank', 'The rest']]} />
        <Step>Record it with an amount of $2,000 and the app posts:</Step>
        <Entry lines={[['Rent, Abuja', '$1,200.00', ''], ['Rent, Kano', '$800.00', ''], ['Bank', '', '$2,000.00']]} />
        <p>If the rent goes up to $2,200, record $2,200 and the split follows: $1,320 and $880.</p>
      </Part>
      <Part title="Example 3: a loan repayment (fixed amount)">
        <p>Each month you pay the bank $500: $50 of it is interest and the rest reduces the loan.</p>
        <Step>Set it up once</Step>
        <Setup lines={[['Debit', 'Interest expense', 'Fixed $50.00'], ['Debit', 'Bank loan (a liability)', 'The rest'], ['Credit', 'Bank', 'The rest']]} />
        <Step>Record it with an amount of $500 and the app posts:</Step>
        <Entry lines={[['Interest expense', '$50.00', ''], ['Bank loan', '$450.00', ''], ['Bank', '', '$500.00']]} />
        <p>A fixed amount can’t be more than the amount you record: for this type, an amount under $50 is refused.</p>
      </Part>
      <Part title="Can change: choose the account when you record it">
        <p>Tick <em>Can change</em> on a line to let whoever records it pick a different account of the <strong>same kind</strong>.</p>
        <p>For example, tick it on the Cash line of Gift cards sold. A card paid for by transfer can then go to your bank account (1002 or 1003) instead of Cash. An account of another kind, such as an expense in place of a bank account, isn’t allowed.</p>
      </Part>
      <Part title="Recording one, and fixing a mistake">
        <ol>
          <li>Click <em>Record a transaction</em>, or <em>Record</em> beside a type.</li>
          <li>Choose the type, the date and the amount, and add a note if you like (e.g. the gift card number).</li>
          <li>Check the entry shown under <em>This posts</em>, then click Post.</li>
        </ol>
        <p>It goes straight into the books and shows in <Link to="/accounting/journal-entries">Journal entries</Link> as <em>Transaction type</em>. If it’s wrong, open it there, void it, and record it again.</p>
      </Part>
      <Part title="Changing, archiving and deleting a type">
        <p>Changes to a type apply to what you record from then on; entries already posted stay as they were.</p>
        <p>A type that has never been recorded can be deleted. Once it has been recorded, archive it instead: it stops appearing when you record, its past entries stay, and you can restore it at any time.</p>
      </Part>
    </Guide>
  )
}

export function OpeningBalancesGuide() {
  return (
    <Guide id="opening-balances" title="Guide: moving your books in"
      intro="If you used other accounting software, or paper books, before ASYS, enter what each account held on the day you start. Then your reports carry on from your old books instead of starting at zero.">
      <Part title="What you need" open>
        <ol>
          <li>Pick your start date: usually the last day of a month or year, when your old books were up to date.</li>
          <li>Print or export a <strong>trial balance</strong> as of that date from your old system. It lists every account with a debit or credit balance.</li>
          <li>Make sure each account on it is in the <Link to="/master/accounts">chart of accounts</Link>. Add any that are missing, under the right heading.</li>
        </ol>
      </Part>
      <Part title="Entering the balances">
        <p>Choose the <em>As of</em> date, then type each account’s balance in its debit or credit column, just as it appears on your trial balance:</p>
        <ul>
          <li><strong>Debit:</strong> usually what you own (cash, bank, stock, equipment) and costs.</li>
          <li><strong>Credit:</strong> usually what you owe (vendors, loans, sales tax), the owners’ equity and income.</li>
        </ul>
        <p>Save as you go; you can come back and change a balance until its month is closed.</p>
      </Part>
      <Part title="Example">
        <p>Your old trial balance on 30 September shows:</p>
        <Entry lines={[['Bank', '$12,000.00', ''], ['Stock', '$8,000.00', ''], ['Accounts payable', '', '$3,000.00'], ['Owner’s capital', '', '$17,000.00']]} />
        <p>Enter those four balances as of 30 September. Debits ($20,000) equal credits ($20,000), so the page shows <em>Balanced</em> and Opening balance equity is $0.</p>
      </Part>
      <Part title="Opening balance equity and the balance check">
        <p>Each balance is saved as its own entry with the other side in <strong>Opening balance equity</strong>, so the books always balance while you work. When every account is in and your old books balanced, it comes to zero.</p>
        <p>If the page says <em>Out by</em> an amount, debits and credits don’t match yet: an account may be missing, in the wrong column, or mistyped. The difference waits in Opening balance equity until it’s fixed. If a real difference remains, move it to owner’s equity or retained earnings with a <Link to="/accounting/journal-entries">journal entry</Link>.</p>
      </Part>
      <Part title="Customers, vendors and stock">
        <p>A balance entered on Accounts receivable, Accounts payable or Inventory goes into the books as one amount. It isn’t split by customer, vendor or item, so customer balances and aging, vendor balances and stock value by item won’t include it.</p>
        <p>For those details, enter your customers’ unpaid invoices and your vendors’ unpaid bills, and count your stock; or keep the lump sum and track the detail outside ASYS until it’s cleared.</p>
      </Part>
      <Part title="Changing an opening balance later">
        <p>Change the amount here or on the account’s page and save: the old entry is voided and a new one is posted. Once its month is closed it’s locked, and a change needs a journal entry.</p>
      </Part>
    </Guide>
  )
}
